import { Component, Vue } from 'vue-property-decorator';
import lodash from 'lodash';
import { NOTIFICATION } from '@/constants';
import { displayPrizeType, formatCurrency, sortArrayByDateField } from '@/helpers';
import { RewardItemDTO, RewardItemDisplayDTO, RewardPeriodDTO, SelectionOptions, TableHeaderColumns } from '@/models';
import { deleteRewardItem, fetchRewardPeriodById } from '@/services';
import AddRewardItemDialog from '@/components/pages/reward-period-detail/add-reward-item-dialog/AddRewardItemDialog.vue';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import EditRewardItemDialog from '@/components/pages/reward-period-detail/edit-reward-item-dialog/EditRewardItemDialog.vue';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';

@Component({
  components: {
    AddRewardItemDialog,
    ConfirmationDialog,
    EditRewardItemDialog,
    FilterDropdown,
    Pagination,
    SearchInput
  }
})
export default class RewardPeriodDetailItems extends Vue {
  private imageUrl = process.env.VUE_APP_HIVE_HOME_URL + '/images/';
  private readonly headerTable: TableHeaderColumns = [
    {
      value: 'name',
      text: 'Name'
    },
    {
      value: 'image',
      text: 'Image'
    },
    {
      value: 'description',
      text: 'Description'
    },
    {
      value: 'type',
      text: 'Type'
    },
    {
      align: 'end',
      value: 'estimatedPrice',
      text: 'Estimated Price (VND)'
    },
    {
      align: 'end',
      value: 'cost',
      text: 'Cost (Pollens)'
    },
    {
      align: 'end',
      value: 'quantity',
      text: 'Quantity'
    },
    {
      align: 'end',
      value: 'maxRedeemPerUser',
      text: 'Max per user'
    },
    {
      align: 'end',
      value: 'redeemCount',
      text: 'Redeemed (items)'
    },
    {
      value: 'actions',
      text: 'Actions',
      align: 'end'
    }
  ];
  private loading = false;
  private rewardItems: RewardItemDisplayDTO[] = [];
  private searchPrize = '';
  private pageCount = 0;
  private page = 1;
  private itemsPerPage = 10;
  private prizeTypeOptions: SelectionOptions = [];
  private selectedPrizeTypes = [] as string[];
  private addMorePrizeDialogShown = false;
  private editItemDialogShown = false;
  private editItemDialogData: RewardItemDisplayDTO[] = [];
  private rewardPeriodId = -1;
  private rewardPeriod: RewardPeriodDTO = {} as RewardPeriodDTO;
  private confirmationDeleteDialogShown = false;
  private selectedDeleteItem: RewardItemDisplayDTO = {} as RewardItemDisplayDTO;

  get displayRewardItems(): RewardItemDisplayDTO[] {
    return this.buildDisplayRewardItems();
  }

  buildDisplayRewardItems(): RewardItemDisplayDTO[] {
    let displayRewardItems = this.rewardItems;
    displayRewardItems = this.filterRewardItemsByCreatedAt(displayRewardItems);
    displayRewardItems = this.filterRewardItemsByName(displayRewardItems);
    displayRewardItems = this.filterRewardItemsByType(displayRewardItems);
    return displayRewardItems;
  }

  filterRewardItemsByCreatedAt(rewardItems: RewardItemDisplayDTO[]): RewardItemDisplayDTO[] {
    return sortArrayByDateField(rewardItems || [], 'createdAt');
  }

  filterRewardItemsByName(rewardItems: RewardItemDisplayDTO[]): RewardItemDisplayDTO[] {
    return !lodash.isEmpty(this.searchPrize)
      ? rewardItems.filter(item => item.name.toLowerCase().includes(this.searchPrize))
      : rewardItems;
  }

  filterRewardItemsByType(rewardItems: RewardItemDisplayDTO[]): RewardItemDisplayDTO[] {
    return this.selectedPrizeTypes.length > 0
      ? rewardItems.filter(item => this.selectedPrizeTypes.includes(item.type.toString()))
      : rewardItems;
  }

  onSearch(searchText: string): void {
    this.searchPrize = searchText;
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  onPrizeTypeChange(selectedPrizeTypes: string[]): void {
    this.selectedPrizeTypes = selectedPrizeTypes;
  }

  onConfirmChanged(): void {
    this.setInitialRewardItems();
    this.addMorePrizeDialogShown = false;
  }

  setInitialPrizeTypeOptions(): void {
    lodash.uniqBy(this.rewardItems, 'type').forEach(item => {
      this.prizeTypeOptions.push({ text: this.getPrizeType(item.type), value: item.type });
    });
  }

  async setInitialRewardItems(): Promise<void> {
    const { data } = await fetchRewardPeriodById(this.rewardPeriodId);
    this.rewardItems = [];
    if (data.rewardItems) {
      data.rewardItems.forEach((item: RewardItemDTO) => {
        this.rewardItems.push({
          id: item.id,
          prizeId: item.prize.id,
          name: item.prize.name,
          image: item.prize.image,
          description: item.prize.description,
          type: item.prize.type,
          cost: item.cost,
          quantity: item.quantity,
          redeemCount: item.redeemCount,
          maxRedeemPerUser: item.maxRedeemPerUser,
          estimatedPrice: item.estimatedPrice,
          createdAt: item.createdAt
        });
      });
    }
  }

  openEditDialog(item: RewardItemDisplayDTO): void {
    this.editItemDialogData = [item];
    this.editItemDialogShown = true;
  }

  closeEditDialog(): void {
    this.editItemDialogData = [];
    this.setInitialRewardItems();
    this.editItemDialogShown = false;
  }

  openDeleteDialog(rewardItem: RewardItemDisplayDTO): void {
    this.selectedDeleteItem = rewardItem;
    this.confirmationDeleteDialogShown = true;
  }

  getPrizeType(type: string): string {
    return displayPrizeType(type);
  }

  getImageSource(imgStr: string): string {
    return this.imageUrl + imgStr;
  }

  formatCurrency(amount: number): string {
    return formatCurrency(amount);
  }

  addNotification(
    type: string,
    title: string,
    content: string[],
    icon: string,
    multiline: boolean,
    height: number
  ): void {
    const notification = { showing: true, type, title, content, icon, multiline, height };
    this.$store.commit('notification/setNotification', notification);
  }

  async deleteRewardItem(): Promise<void> {
    if (this.selectedDeleteItem.id) {
      const resultDeleteRewardItem = await deleteRewardItem(this.selectedDeleteItem.id);
      if (resultDeleteRewardItem.status === 200) {
        this.rewardItems = this.rewardItems.filter(item => item.id !== this.selectedDeleteItem.id);
        this.addNotification(
          NOTIFICATION.TYPE.success,
          NOTIFICATION.TEXT.removeRewardItemSuccess,
          [],
          NOTIFICATION.ICON.success,
          false,
          0
        );
        this.$store.commit('rewardPeriod/deleteRewardItem', this.selectedDeleteItem.id);
      } else {
        this.addNotification(
          NOTIFICATION.TYPE.error,
          NOTIFICATION.TEXT.removeRewardItemFailed,
          [],
          NOTIFICATION.ICON.error,
          false,
          0
        );
      }
    } else {
      this.addNotification(
        NOTIFICATION.TYPE.error,
        NOTIFICATION.TEXT.removeRewardItemFailed,
        [],
        NOTIFICATION.ICON.error,
        false,
        0
      );
    }
    this.selectedDeleteItem = {} as RewardItemDisplayDTO;
    this.confirmationDeleteDialogShown = false;
  }

  async setInitialRewardPeriod(): Promise<void> {
    const { data } = await fetchRewardPeriodById(this.rewardPeriodId);
    this.rewardPeriod = data;
  }

  async created(): Promise<void> {
    this.loading = true;
    this.rewardPeriodId = parseInt(this.$route.params.id);
    await this.setInitialRewardPeriod();
    await this.setInitialRewardItems();
    this.setInitialPrizeTypeOptions();
    this.loading = false;
  }
}
